import type { PublishResult } from "@magic-md/engine-client/protocol";
import { compileProfileStyleVariables } from "../theme/runtime/profile-resolver";
import { compileThemeStyleVariables } from "../theme/runtime/theme-compiler";
import { useThemeStore } from "../theme/runtime/theme-store";

function normalizePath(path: string): string {
  return path.replace(/\\/g, "/");
}

function normalizeComparablePath(path: string): string {
  return normalizePath(path).replace(/\/+$/, "");
}

export interface DocumentPublishPresentationSnapshot {
  themeId: string;
  contentProfileId: string;
  presentationProfileId: string;
}

export interface DocumentPublishSettings {
  outDir: string;
  clean: boolean;
  rerunAll: boolean;
  useOpenBufferContent: boolean;
}

export interface DocumentPublishConfigFile {
  version: 1;
  kind: "magic-md.document-publish.config";
  filePath: string;
  updatedAt: string;
  settings: DocumentPublishSettings;
  presentation: DocumentPublishPresentationSnapshot;
}

export interface DocumentPublishStatusFile {
  version: 1;
  kind: "magic-md.document-publish.status";
  filePath: string;
  taskId: string | null;
  status: "running" | "completed" | "failed" | "cancelled";
  updatedAt: string;
  progress: { message: string; percent: number } | null;
  outputPath: string | null;
  generatedAt: string | null;
  error: string | null;
}

export interface DocumentPublishReportFile {
  version: 1;
  kind: "magic-md.document-publish.report";
  filePath: string;
  taskId: string | null;
  status: "running" | "completed" | "failed" | "cancelled";
  updatedAt: string;
  result: PublishResult | null;
  error: string | null;
}

// ── Folder-level publish types ──

export interface FolderPublishSettings {
  outDir: string;
  clean: boolean;
  rerunAll: boolean;
  incremental: boolean;
  siteTitle: string;
}

export interface FolderPublishConfigFile {
  version: 1;
  kind: "magic-md.folder-publish.config";
  folderPath: string;
  updatedAt: string;
  settings: FolderPublishSettings;
}

export interface FolderPublishStatusFile {
  version: 1;
  kind: "magic-md.folder-publish.status";
  folderPath: string;
  taskId: string | null;
  status: "running" | "completed" | "failed" | "cancelled";
  updatedAt: string;
  progress: { message: string; percent: number } | null;
  outputPath: string | null;
  generatedAt: string | null;
  error: string | null;
}

export interface FolderPublishReportFile {
  version: 1;
  kind: "magic-md.folder-publish.report";
  folderPath: string;
  taskId: string | null;
  status: "running" | "completed" | "failed" | "cancelled";
  updatedAt: string;
  result: PublishResult | null;
  error: string | null;
}

// ── Metadata file constants ──

export const DOCUMENT_PUBLISH_METADATA_FILES = [
  {
    name: "publish.status.json",
    getPath: (bundleDir: string) =>
      `${normalizePath(bundleDir)}/publish/publish.status.json`,
  },
  {
    name: "publish.config.json",
    getPath: (bundleDir: string) =>
      `${normalizePath(bundleDir)}/publish/publish.config.json`,
  },
  {
    name: "publish.report.json",
    getPath: (bundleDir: string) =>
      `${normalizePath(bundleDir)}/publish/publish.report.json`,
  },
] as const;

const DOCUMENT_PUBLISH_METADATA_FILE_NAMES: Set<string> = new Set(
  DOCUMENT_PUBLISH_METADATA_FILES.map((entry) => entry.name),
);

export const FOLDER_PUBLISH_METADATA_FILES = [
  {
    name: "folder-publish.config.json",
    getPath: (publishDir: string) =>
      `${normalizePath(publishDir)}/folder-publish.config.json`,
  },
  {
    name: "folder-publish.status.json",
    getPath: (publishDir: string) =>
      `${normalizePath(publishDir)}/folder-publish.status.json`,
  },
  {
    name: "folder-publish.report.json",
    getPath: (publishDir: string) =>
      `${normalizePath(publishDir)}/folder-publish.report.json`,
  },
] as const;

const FOLDER_PUBLISH_METADATA_FILE_NAMES: Set<string> = new Set(
  FOLDER_PUBLISH_METADATA_FILES.map((entry) => entry.name),
);

// ── Folder publish path helpers ──

export function getFolderPublishDir(folderPath: string): string {
  return `${normalizePath(folderPath)}/.magic-md/publish`;
}

export function getFolderPublishConfigPath(folderPath: string): string {
  return `${getFolderPublishDir(folderPath)}/folder-publish.config.json`;
}

export function getFolderPublishStatusPath(folderPath: string): string {
  return `${getFolderPublishDir(folderPath)}/folder-publish.status.json`;
}

export function getFolderPublishReportPath(folderPath: string): string {
  return `${getFolderPublishDir(folderPath)}/folder-publish.report.json`;
}

export function resolveFolderPublishOutDir(folderPath: string): string {
  return `${getFolderPublishDir(folderPath)}/dist`;
}

export function isFolderPublishDirPath(filePath: string): boolean {
  return /\/\.magic-md\/publish$/i.test(normalizePath(filePath));
}

export function getFolderPathFromPublishDir(
  filePath: string,
): string | null {
  if (!isFolderPublishDirPath(filePath)) return null;
  return normalizePath(filePath).replace(/\/\.magic-md\/publish$/i, "");
}

export function hasFolderPublishMetadataEntries(
  entries: Array<{ name: string; isDirectory: boolean }>,
): boolean {
  return entries.some(
    (entry) =>
      !entry.isDirectory &&
      FOLDER_PUBLISH_METADATA_FILE_NAMES.has(entry.name),
  );
}

export function isFolderPublishMetadataName(name: string): boolean {
  return FOLDER_PUBLISH_METADATA_FILE_NAMES.has(name);
}

// ── Folder publish file factories ──

export function createFolderPublishConfigFile(params: {
  folderPath: string;
  settings: FolderPublishSettings;
  updatedAt?: string;
}): FolderPublishConfigFile {
  return {
    version: 1,
    kind: "magic-md.folder-publish.config",
    folderPath: normalizePath(params.folderPath),
    updatedAt: params.updatedAt ?? new Date().toISOString(),
    settings: {
      ...params.settings,
      outDir: normalizePath(params.settings.outDir),
    },
  };
}

export function createFolderPublishStatusFile(params: {
  folderPath: string;
  taskId?: string;
  status: FolderPublishStatusFile["status"];
  progress?: { message: string; percent: number };
  outputPath?: string;
  generatedAt?: string;
  error?: string;
  updatedAt?: string;
}): FolderPublishStatusFile {
  return {
    version: 1,
    kind: "magic-md.folder-publish.status",
    folderPath: normalizePath(params.folderPath),
    taskId: params.taskId ?? null,
    status: params.status,
    updatedAt: params.updatedAt ?? new Date().toISOString(),
    progress: params.progress ?? null,
    outputPath: params.outputPath ? normalizePath(params.outputPath) : null,
    generatedAt: params.generatedAt ?? null,
    error: params.error ?? null,
  };
}

export function createFolderPublishReportFile(params: {
  folderPath: string;
  taskId?: string;
  status: FolderPublishReportFile["status"];
  result?: PublishResult;
  error?: string;
  updatedAt?: string;
}): FolderPublishReportFile {
  return {
    version: 1,
    kind: "magic-md.folder-publish.report",
    folderPath: normalizePath(params.folderPath),
    taskId: params.taskId ?? null,
    status: params.status,
    updatedAt: params.updatedAt ?? new Date().toISOString(),
    result: params.result ?? null,
    error: params.error ?? null,
  };
}

export function parseFolderPublishConfigFile(
  content: string,
  folderPath: string,
): FolderPublishConfigFile | null {
  try {
    const parsed = JSON.parse(content) as Partial<FolderPublishConfigFile>;
    if (
      parsed.kind !== "magic-md.folder-publish.config" ||
      !parsed.settings ||
      typeof parsed.settings.outDir !== "string"
    ) {
      return null;
    }
    return createFolderPublishConfigFile({
      folderPath,
      settings: {
        outDir: parsed.settings.outDir,
        clean: !!parsed.settings.clean,
        rerunAll: !!parsed.settings.rerunAll,
        incremental: parsed.settings.incremental !== false,
        siteTitle: parsed.settings.siteTitle ?? "",
      },
      updatedAt:
        typeof parsed.updatedAt === "string" ? parsed.updatedAt : undefined,
    });
  } catch {
    return null;
  }
}

export function isMarkdownDocumentPath(
  filePath: string | null | undefined,
): filePath is string {
  return typeof filePath === "string" && /\.md$/i.test(filePath);
}

export function getPathDirname(filePath: string): string {
  const normalized = normalizePath(filePath);
  const lastSlash = normalized.lastIndexOf("/");
  if (lastSlash < 0) return "";
  return lastSlash === 0 ? "/" : normalized.slice(0, lastSlash);
}

export function getDocumentBundleDir(filePath: string): string {
  const normalized = normalizePath(filePath);
  const dir = getPathDirname(normalized);
  const base = normalized.slice(dir === "/" ? 1 : dir.length + 1);
  const dot = base.lastIndexOf(".");
  const stem = dot > 0 ? base.slice(0, dot) : base;
  return dir ? `${dir}/${stem}.magic-md` : `${stem}.magic-md`;
}

export function getDocumentPublishDir(filePath: string): string {
  return `${getDocumentBundleDir(filePath)}/publish`;
}

export function getDocumentAssetsDir(filePath: string): string {
  return `${getDocumentBundleDir(filePath)}/assets`;
}

export function getBundlePublishConfigPath(bundleDir: string): string {
  return `${normalizePath(bundleDir)}/publish/publish.config.json`;
}

export function getBundlePublishStatusPath(bundleDir: string): string {
  return `${normalizePath(bundleDir)}/publish/publish.status.json`;
}

export function getBundlePublishReportPath(bundleDir: string): string {
  return `${normalizePath(bundleDir)}/publish/publish.report.json`;
}

export function getDocumentPublishConfigPath(filePath: string): string {
  return getBundlePublishConfigPath(getDocumentBundleDir(filePath));
}

export function getDocumentPublishStatusPath(filePath: string): string {
  return getBundlePublishStatusPath(getDocumentBundleDir(filePath));
}

export function getDocumentPublishReportPath(filePath: string): string {
  return getBundlePublishReportPath(getDocumentBundleDir(filePath));
}

export function listBundlePublishMetadataPaths(bundleDir: string): string[] {
  return DOCUMENT_PUBLISH_METADATA_FILES.map((entry) =>
    entry.getPath(bundleDir),
  );
}

export function hasDocumentPublishMetadataEntries(
  entries: Array<{ name: string; isDirectory: boolean }>,
): boolean {
  return entries.some(
    (entry) =>
      !entry.isDirectory &&
      DOCUMENT_PUBLISH_METADATA_FILE_NAMES.has(entry.name),
  );
}

export function isDocumentPublishMetadataName(name: string): boolean {
  return DOCUMENT_PUBLISH_METADATA_FILE_NAMES.has(name);
}

export function resolveSingleDocPublishOutDir(
  filePath: string | null | undefined,
): string | undefined {
  return isMarkdownDocumentPath(filePath)
    ? `${getDocumentPublishDir(filePath)}/dist`
    : undefined;
}

export function normalizeSingleDocPublishOutDir(
  filePath: string,
  outDir: string | null | undefined,
): string | undefined {
  const defaultOutDir = resolveSingleDocPublishOutDir(filePath);
  if (!defaultOutDir) return undefined;
  const candidate = outDir?.trim()
    ? normalizePath(outDir.trim())
    : defaultOutDir;
  const publishPackageDir = getDocumentPublishDir(filePath);
  return normalizeComparablePath(candidate) ===
    normalizeComparablePath(publishPackageDir)
    ? defaultOutDir
    : candidate;
}

export function getPublishPresentationStyleVariables(params: {
  themeId: string;
  contentProfileId: string;
  presentationProfileId: string;
}): Record<string, string> {
  const themeVariables = compileThemeStyleVariables({
    themeId: params.themeId,
  }).styleVariables;
  const profileVariables = compileProfileStyleVariables(
    params.contentProfileId,
    params.presentationProfileId,
  );
  return {
    ...themeVariables,
    ...profileVariables,
  };
}

export function getCurrentPublishPresentation(): {
  presentationProfileId: string;
  presentationStyleVariables: Record<string, string>;
} {
  const { contentProfile, presentationProfile, resolvedThemeId } =
    useThemeStore.getState();
  return {
    presentationProfileId: presentationProfile,
    presentationStyleVariables: getPublishPresentationStyleVariables({
      themeId: resolvedThemeId,
      contentProfileId: contentProfile,
      presentationProfileId: presentationProfile,
    }),
  };
}

export function getCurrentPublishPresentationSnapshot(): DocumentPublishPresentationSnapshot {
  const { contentProfile, presentationProfile, resolvedThemeId } =
    useThemeStore.getState();
  return {
    themeId: resolvedThemeId,
    contentProfileId: contentProfile,
    presentationProfileId: presentationProfile,
  };
}

export function getBundleDirFromPublishOutputPath(
  outputPath: string,
): string | null {
  const normalized = normalizePath(outputPath);
  const marker = ".magic-md/publish";
  const markerIndex = normalized.indexOf(marker);
  if (markerIndex < 0) return null;
  return normalized.slice(0, markerIndex + ".magic-md".length);
}

export function getPublishDirFromOutputPath(outputPath: string): string | null {
  const bundleDir = getBundleDirFromPublishOutputPath(outputPath);
  return bundleDir ? `${bundleDir}/publish` : null;
}

export function isDocumentPublishDirPath(filePath: string): boolean {
  return /\.magic-md\/publish$/i.test(normalizePath(filePath));
}

export function isDocumentAssetsDirPath(filePath: string): boolean {
  return /\.magic-md\/assets$/i.test(normalizePath(filePath));
}

export function isDocumentAssetsPath(filePath: string): boolean {
  return /\.magic-md\/assets(?:\/|$)/i.test(normalizePath(filePath));
}

export function getBundleDirFromPublishDirPath(
  filePath: string,
): string | null {
  if (!isDocumentPublishDirPath(filePath)) return null;
  return normalizePath(filePath).replace(/\/publish$/i, "");
}

export function getBundleDirFromAssetsDirPath(filePath: string): string | null {
  if (!isDocumentAssetsDirPath(filePath)) return null;
  return normalizePath(filePath).replace(/\/assets$/i, "");
}

export function getDocumentPathFromAssetsPath(filePath: string): string | null {
  const normalized = normalizePath(filePath);
  const marker = ".magic-md/assets";
  const idx = normalized.indexOf(marker);
  if (idx < 0) return null;
  const bundlePath = normalized.slice(0, idx + ".magic-md".length);
  return bundlePath.replace(/\.magic-md$/i, ".md");
}

export function getAssetRelativePathFromPath(filePath: string): string | null {
  const normalized = normalizePath(filePath);
  const marker = ".magic-md/assets/";
  const idx = normalized.indexOf(marker);
  if (idx < 0) return null;
  const relativePath = normalized.slice(idx + marker.length);
  return relativePath.length > 0 ? relativePath : null;
}

export function getAssetRefFromPath(filePath: string): string | null {
  const relativePath = getAssetRelativePathFromPath(filePath);
  return relativePath ? `$ref{assets/${relativePath}}` : null;
}

export function createDocumentPublishConfigFile(params: {
  filePath: string;
  settings: DocumentPublishSettings;
  presentation: DocumentPublishPresentationSnapshot;
  updatedAt?: string;
}): DocumentPublishConfigFile {
  return {
    version: 1,
    kind: "magic-md.document-publish.config",
    filePath: normalizePath(params.filePath),
    updatedAt: params.updatedAt ?? new Date().toISOString(),
    settings: {
      ...params.settings,
      outDir: normalizePath(params.settings.outDir),
    },
    presentation: params.presentation,
  };
}

export function createDocumentPublishStatusFile(params: {
  filePath: string;
  taskId?: string;
  status: DocumentPublishStatusFile["status"];
  progress?: { message: string; percent: number };
  outputPath?: string;
  generatedAt?: string;
  error?: string;
  updatedAt?: string;
}): DocumentPublishStatusFile {
  return {
    version: 1,
    kind: "magic-md.document-publish.status",
    filePath: normalizePath(params.filePath),
    taskId: params.taskId ?? null,
    status: params.status,
    updatedAt: params.updatedAt ?? new Date().toISOString(),
    progress: params.progress ?? null,
    outputPath: params.outputPath ? normalizePath(params.outputPath) : null,
    generatedAt: params.generatedAt ?? null,
    error: params.error ?? null,
  };
}

export function createDocumentPublishReportFile(params: {
  filePath: string;
  taskId?: string;
  status: DocumentPublishReportFile["status"];
  result?: PublishResult;
  error?: string;
  updatedAt?: string;
}): DocumentPublishReportFile {
  return {
    version: 1,
    kind: "magic-md.document-publish.report",
    filePath: normalizePath(params.filePath),
    taskId: params.taskId ?? null,
    status: params.status,
    updatedAt: params.updatedAt ?? new Date().toISOString(),
    result: params.result ?? null,
    error: params.error ?? null,
  };
}

export function parseDocumentPublishConfigFile(
  content: string,
  filePath: string,
): DocumentPublishConfigFile | null {
  try {
    const parsed = JSON.parse(content) as Partial<DocumentPublishConfigFile>;
    if (
      parsed.kind !== "magic-md.document-publish.config" ||
      !parsed.settings ||
      typeof parsed.settings.outDir !== "string"
    ) {
      return null;
    }
    return createDocumentPublishConfigFile({
      filePath,
      settings: {
        outDir: parsed.settings.outDir,
        clean: !!parsed.settings.clean,
        rerunAll: !!parsed.settings.rerunAll,
        useOpenBufferContent: parsed.settings.useOpenBufferContent !== false,
      },
      presentation: {
        themeId: parsed.presentation?.themeId ?? "default",
        contentProfileId: parsed.presentation?.contentProfileId ?? "default",
        presentationProfileId:
          parsed.presentation?.presentationProfileId ?? "default",
      },
      updatedAt:
        typeof parsed.updatedAt === "string" ? parsed.updatedAt : undefined,
    });
  } catch {
    return null;
  }
}
